import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SIDEBAR_ACTIONS } from '../../../../../../../../constants/actionsConstants';
import { useTools } from '../../../hooks/useTools';
import { useTeams } from '../../../../../../../hooks/useTeams';
import { useFunnels } from '../../../../../../../hooks/useFunnels';
import MediaModel from '../../../../../../../models/MediaModel';
import WhatsappFlowModelMC from '../../../../../../../models/WhatsappFlowModelMC';
import ChatbotOptionModel from '../../../../../../../../components/chatbots/ChatbotOptionModel';
import { purchaseIntentionArgs } from '../../utils/purchaseIntentionAiDisabled';
import { retrieveProductsArgs } from '../../utils/retrieveProductsIncludeImages';
import { getDetailViewForToolId, TOOL_DETAIL_SELECT_CONFIG } from '../constants/toolModalNavigation';
import useApplyInitialArgsOnce from './globalInstructionToolsModal/useApplyInitialArgsOnce';
import useInitialToolArgsSync from './globalInstructionToolsModal/useInitialToolArgsSync';
import useRetailerApiRequestTool from './globalInstructionToolsModal/useRetailerApiRequestTool';

const useGlobalInstructionToolsModal = ({
  onSelectTool,
  mode = 'select',
  initialToolId = null,
  initialArgs = null,
  submitToolLabel = null
}) => {
  const { t } = useTranslation(['ChatbotIA', 'Common']);
  const { tools, getToolDetails } = useTools();
  const { teamOptions } = useTeams(true);
  const { funnelOptions } = useFunnels(true);
  const dispatch = useDispatch();
  const [currentView, setCurrentView] = useState('list');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [purchaseIntentionAiDisabled, setPurchaseIntentionAiDisabled] = useState(true);
  const [retrieveProductsIncludeImages, setRetrieveProductsIncludeImages] = useState(true);
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [whatsappFlowItems, setWhatsappFlowItems] = useState([]);
  const [whatsappFlowsLoading, setWhatsappFlowsLoading] = useState(false);
  const [selectedWhatsappFlowId, setSelectedWhatsappFlowId] = useState(null);
  const [crmFieldOptions, setCrmFieldOptions] = useState([]);
  const [crmFieldsLoading, setCrmFieldsLoading] = useState(false);
  const [selectedCrmFieldIds, setSelectedCrmFieldIds] = useState([]);

  const initialTool = useMemo(() => {
    if (!initialToolId) return null;
    // Use full catalog so saved tools open even when not in the agent's active-tool list.
    return getToolDetails(initialToolId);
  }, [initialToolId, getToolDetails]);

  const { appliedInitialArgsRef, resetAppliedInitialArgs } = useApplyInitialArgsOnce(initialToolId);

  const closeAction = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: {
        componentKey: 'GlobalInstructionToolsModal'
      }
    });
  }, [dispatch]);

  const retailerApiRequestTool = useRetailerApiRequestTool({
    t,
    mode,
    onSelectTool,
    selectedTool,
    closeAction,
    initialTool,
    initialArgs,
    appliedInitialArgsRef,
    resetAppliedInitialArgs
  });
  const resetRetailerApiRequestTool = retailerApiRequestTool.reset;

  const handleSelect = useCallback((tool) => {
    if (tool?.requiresSubscription || tool?.isAvailable === false) return;

    setSelectedTool(tool);

    const detail = TOOL_DETAIL_SELECT_CONFIG[tool.id];
    if (detail) {
      const isEditingSameTool = initialToolId === tool.id && initialArgs != null;
      if (detail.resetRetailerDraft && !isEditingSameTool) {
        resetRetailerApiRequestTool();
      }
      setCurrentView(detail.view);
      return;
    }

    if (mode !== 'browse' && onSelectTool) {
      onSelectTool({ name: tool.id, args: [], ...tool });
    }
    if (mode !== 'browse') {
      closeAction();
    }
  }, [initialArgs, initialToolId, onSelectTool, closeAction, mode, resetRetailerApiRequestTool]);

  const handleBack = useCallback(() => {
    setCurrentView('list');
    setSelectedTeam(null);
    setSelectedFunnel(null);
    setSelectedStep(null);
    setSelectedFileIds([]);
    setSelectedWhatsappFlowId(null);
    setPurchaseIntentionAiDisabled(true);
    setRetrieveProductsIncludeImages(true);
    setSelectedCrmFieldIds([]);
    resetRetailerApiRequestTool();
  }, [resetRetailerApiRequestTool]);

  const handleConfirmAssignment = useCallback(() => {
    if (mode === 'browse' || !onSelectTool || !selectedTeam || !selectedTool) return;

    onSelectTool({
      ...selectedTool,
      name: selectedTool.id,
      args: [{ assignable_id: String(selectedTeam), assignable_type: 'team' }]
    });
    closeAction();
  }, [closeAction, mode, onSelectTool, selectedTeam, selectedTool]);

  const handleConfirmNegotiation = useCallback(() => {
    const stepOptions = funnelOptions.find((opt) => opt.id === selectedFunnel)?.funnel_steps || [];
    const stepOption = stepOptions.find((opt) => opt.id === selectedStep);

    if (mode !== 'browse' && onSelectTool && selectedFunnel && selectedStep && selectedTool) {
      onSelectTool({
        ...selectedTool,
        name: selectedTool.id,
        args: [{
          funnel_id: selectedFunnel,
          funnel_step_id: selectedStep,
          funnel_step_web_id: stepOption?.web_id
        }]
      });
      closeAction();
    }
  }, [closeAction, funnelOptions, mode, onSelectTool, selectedFunnel, selectedStep, selectedTool]);

  const handleConfirmPurchaseIntention = useCallback(() => {
    if (mode === 'browse' || !onSelectTool || !selectedTool) return;

    onSelectTool({
      ...selectedTool,
      name: selectedTool.id,
      args: purchaseIntentionArgs(purchaseIntentionAiDisabled)
    });
    closeAction();
  }, [closeAction, mode, onSelectTool, purchaseIntentionAiDisabled, selectedTool]);

  const handleConfirmRetrieveProducts = useCallback(() => {
    if (mode === 'browse' || !onSelectTool || !selectedTool) return;

    onSelectTool({
      ...selectedTool,
      name: selectedTool.id,
      args: retrieveProductsArgs(retrieveProductsIncludeImages)
    });
    closeAction();
  }, [closeAction, mode, onSelectTool, retrieveProductsIncludeImages, selectedTool]);

  const fetchMedia = useCallback(() => {
    setMediaLoading(true);
    MediaModel.get({ limit: 50, sort: '-created_at' })
      .then((res) => {
        const data = res?.data || res?.items || res?.results || res;
        setMediaItems(Array.isArray(data) ? data : (data?.items || []));
      })
      .catch(() => {
        setMediaItems([]);
      })
      .finally(() => setMediaLoading(false));
  }, []);

  const uploadNewMedia = useCallback(async (file, name) => {
    if (!file) return null;
    setUploadingMedia(true);
    try {
      const res = await MediaModel.upload(file, name);
      const created = res?.data || res;
      const createdId = created?.id || created?._id || null;
      await fetchMedia();
      if (createdId) {
        setSelectedFileIds((prev) => {
          const idStr = String(createdId);
          if (prev.includes(idStr)) return prev;
          if (prev.length >= 5) return prev;
          return [...prev, idStr];
        });
      }
      return createdId;
    } finally {
      setUploadingMedia(false);
    }
  }, [fetchMedia]);

  const toggleFileId = useCallback((id) => {
    if (!id) return;
    setSelectedFileIds((prev) => {
      const exists = prev.includes(id);
      if (exists) return prev.filter((x) => x !== id);
      if (prev.length >= 5) return prev;
      return [...prev, id];
    });
  }, []);

  const handleConfirmSendFile = useCallback(() => {
    if (mode === 'browse' || !onSelectTool || !selectedTool) return;
    if (!selectedFileIds || selectedFileIds.length === 0) return;

    onSelectTool({
      ...selectedTool,
      name: selectedTool.id,
      args: selectedFileIds.map((id) => ({ file_id: String(id) }))
    });
    closeAction();
  }, [closeAction, mode, onSelectTool, selectedFileIds, selectedTool]);

  const fetchWhatsappFlows = useCallback(() => {
    setWhatsappFlowsLoading(true);
    WhatsappFlowModelMC.get({ status: 'PUBLISHED' })
      .then((res) => {
        const data = res?.whatsapp_flows || res?.data || res;
        setWhatsappFlowItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setWhatsappFlowItems([]);
      })
      .finally(() => setWhatsappFlowsLoading(false));
  }, []);

  const handleConfirmTriggerWhatsappFlow = useCallback(() => {
    if (mode === 'browse' || !onSelectTool || !selectedTool) return;
    if (!selectedWhatsappFlowId) return;

    onSelectTool({
      ...selectedTool,
      name: selectedTool.id,
      args: [{ whatsapp_flow_id: String(selectedWhatsappFlowId) }]
    });
    closeAction();
  }, [closeAction, mode, onSelectTool, selectedTool, selectedWhatsappFlowId]);

  const fetchCrmFields = useCallback(() => {
    setCrmFieldsLoading(true);
    ChatbotOptionModel.getCustomerColumns()
      .then((response) => {
        const fields = response?.fields || [];
        setCrmFieldOptions(
          fields
            .map((field) => {
              const label = Array.isArray(field) ? field[0] : field?.label;
              const id = Array.isArray(field) ? field[1] : field?.identifier || field?.value;
              if (!id) return null;
              return { id: String(id), label: String(label || id) };
            })
            .filter(Boolean)
        );
      })
      .catch(() => {
        setCrmFieldOptions([]);
      })
      .finally(() => setCrmFieldsLoading(false));
  }, []);

  const toggleCrmFieldId = useCallback((id) => {
    if (!id) return;
    setSelectedCrmFieldIds((prev) => {
      const exists = prev.includes(id);
      if (exists) return prev.filter((fieldId) => fieldId !== id);
      if (prev.length >= 100) return prev;
      return [...prev, id];
    });
  }, []);

  const handleConfirmGetCustomerCrmData = useCallback(() => {
    if (mode === 'browse' || !onSelectTool || !selectedTool) return;
    if (!selectedCrmFieldIds || selectedCrmFieldIds.length === 0) return;

    onSelectTool({
      ...selectedTool,
      name: selectedTool.id,
      args: [{ fields: selectedCrmFieldIds }]
    });
    closeAction();
  }, [closeAction, mode, onSelectTool, selectedCrmFieldIds, selectedTool]);

  useEffect(() => {
    if (initialToolId) return;
    setCurrentView('list');
    setSelectedTool(null);
  }, [initialToolId]);

  useEffect(() => {
    if (!initialTool) return;
    setSelectedTool(initialTool);
    setCurrentView(getDetailViewForToolId(initialTool.id));
  }, [initialTool]);

  useInitialToolArgsSync({
    initialTool,
    initialArgs,
    teamOptions,
    funnelOptions,
    setSelectedTeam,
    setSelectedFileIds,
    setSelectedWhatsappFlowId,
    setSelectedFunnel,
    setSelectedStep,
    setPurchaseIntentionAiDisabled,
    setRetrieveProductsIncludeImages,
    setSelectedCrmFieldIds,
    appliedInitialArgsRef
  });

  useEffect(() => {
    if (currentView !== 'send_file') return;
    fetchMedia();
  }, [currentView, fetchMedia]);

  useEffect(() => {
    if (currentView !== 'trigger_whatsapp_flow') return;
    fetchWhatsappFlows();
  }, [currentView, fetchWhatsappFlows]);

  useEffect(() => {
    if (currentView !== 'get_customer_crm_data') return;
    fetchCrmFields();
  }, [currentView, fetchCrmFields]);

  return {
    t,
    tools,
    closeAction,
    handleSelect,
    currentView,
    selectedTool,
    selectedTeam,
    teamOptions,
    setSelectedTeam,
    selectedFunnel,
    funnelOptions,
    setSelectedFunnel,
    selectedStep,
    setSelectedStep,
    purchaseIntentionAiDisabled,
    setPurchaseIntentionAiDisabled,
    retrieveProductsIncludeImages,
    setRetrieveProductsIncludeImages,
    mediaItems,
    mediaLoading,
    selectedFileIds,
    toggleFileId,
    handleConfirmSendFile,
    uploadingMedia,
    uploadNewMedia,
    whatsappFlowItems,
    whatsappFlowsLoading,
    selectedWhatsappFlowId,
    setSelectedWhatsappFlowId,
    handleConfirmTriggerWhatsappFlow,
    handleBack,
    handleConfirmAssignment,
    handleConfirmNegotiation,
    handleConfirmPurchaseIntention,
    handleConfirmRetrieveProducts,
    retailerApiRequestTool,
    crmFieldOptions,
    crmFieldsLoading,
    selectedCrmFieldIds,
    toggleCrmFieldId,
    handleConfirmGetCustomerCrmData
  };
};

export default useGlobalInstructionToolsModal;
